import * as tslib_1 from "tslib";
import { AfterContentInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { DomainService } from '../../domain.service';
import { AlertsService } from '../../core/alerts.service';
import { OverlayService } from '../../core/overlay.service';
import { OverlayProvider } from '../../ui/overlay-provider';
import { ForecastListComponent } from '../forecast-list/forecast-list.component';
import { ForecastService } from '../forecast.service';
var ShrinkageComponent = /** @class */ (function () {
    /**
     * Creates an instance of ShrinkageComponent.
     * @param {OverlayProvider} overlayProvider contains the day of the week and the selected function.
     * @param {ForecastService} forecastService is used to handle the shrinkage entities with the back-end.
     * @param {AlertsService} alertsService is used to display alerts.
     * @memberOf ShrinkageComponent
     */
    function ShrinkageComponent(overlayProvider, overlayService, forecastService, alertsService) {
        this.overlayService = overlayService;
        this.forecastService = forecastService;
        this.alertsService = alertsService;
        /**
         * Configuration for the list component displaying existing shrinkages.
         * @type {ComponentConfiguration}
         * @memberOf ShrinkageComponent
         */
        this.listConfiguration = {
            data: {
                entity: 'Shrinkage',
                dataSource: 'DayShrinkages',
                listSize: 10,
                // The values are set in the constructor.
                parameters: {
                    day: null,
                    oid: null
                },
                fields: [
                    {
                        name: 'Start',
                        editor: 'duration',
                        labelKey: 'APP.MISC.SHRINKAGE.START'
                    },
                    {
                        name: 'Finish',
                        editor: 'duration',
                        labelKey: 'APP.MISC.SHRINKAGE.FINISH'
                    },
                    {
                        name: 'Value',
                        editor: 'number',
                        labelKey: 'APP.MISC.SHRINKAGE.VALUE'
                    }
                ]
            },
            rights: ['Read']
        };
        /**
         * The configuration for the starting time of the new shrinkage.
         * @type {FieldConfiguration}
         * @memberOf ShrinkageComponent
         */
        this.startField = {
            name: 'Start',
            editor: 'duration',
            labelKey: 'APP.MISC.SHRINKAGE.START',
            helpKey: 'APP.MISC.SHRINKAGE.START?'
        };
        /**
         * The configuration for the finishing time of the new shrinkage.
         * @type {FieldConfiguration}
         * @memberOf ShrinkageComponent
         */
        this.finishField = {
            name: 'Finish',
            editor: 'duration',
            labelKey: 'APP.MISC.SHRINKAGE.FINISH',
            helpKey: 'APP.MISC.SHRINKAGE.FINISH?'
        };
        /**
         * The configuration for the value of the new shrinkage.
         * @type {FieldConfiguration}
         * @memberOf ShrinkageComponent
         */
        this.valueField = {
            name: 'Value',
            editor: 'number',
            labelKey: 'APP.MISC.SHRINKAGE.VALUE',
            helpKey: 'APP.MISC.SHRINKAGE.VALUE?'
        };
        /**
         * The form group and underlying controls are needed for the angular input validation mechanism.
         * @memberOf ShrinkageComponent
         */
        this.form = new FormGroup({});
        /**
         * The form control binds to the starting time fieldset of the new shrinkage.
         * @memberOf ShrinkageComponent
         */
        this.startControl = new FormControl();
        /**
         * The form control binds to the finishing time fieldsetof the new shrinkage.
         * @memberOf ShrinkageComponent
         */
        this.finishControl = new FormControl();
        /**
         * The form control binds to the value fieldset of the new shrinkage.
         * @memberOf ShrinkageComponent
         */
        this.valueControl = new FormControl();
        /**
         * The shrinkage can be added via a form.
         * @type {Entity}
         * @memberOf ShrinkageComponent
         */
        this.shrinkage = null;
        /**
         * A value indicating whether the first shrinkage was created.
         * @private
         * @memberOf ShrinkageComponent
         */
        this.initialCreated = false;
        if (!overlayProvider.entity) {
            alertsService.createAlert('danger', 'APP.FORECAST.NO_FUNCTION_GIVEN');
            return;
        }
        this.day = Number.parseInt(overlayProvider.action.method);
        var oid = overlayProvider.entity['Oid'];
        this.listConfiguration.data.parameters['day'] = this.day;
        this.listConfiguration.data.parameters['oid'] = oid;
        this.functionKey = DomainService.oidToKey(oid);
        this.shrinkageType = this.forecastService.getEntityType('Shrinkage');
    }
    /**
     * Sets up the first shrinkage in the form.
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.ngAfterContentInit = function () {
        this.resetNewShrinkage();
    };
    /**
     * Closes the overlay.
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.close = function () {
        this.overlayService.closeOverlay();
    };
    /**
     * Adds the first shrinkage in case there is none yet.
     * @param {any} data The query result of the list component refresh operation.
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.listRefresh = function () {
        if (this.listComponent.items.length > 0) {
            return;
        }
        if (this.initialCreated) {
            this.alertsService.createAlert('danger', 'APP.FORECAST.CANNOT_CREATE_SHRINKAGE');
            this.overlayService.closeOverlay();
            return;
        }
        this.initialCreated = true;
        var first = this.listComponent.entityType.createEntity({
            DayOfWeek: this.day,
            FunctionKey: this.functionKey,
            Start: moment.duration().toISOString(),
            Finish: moment.duration('23:59').toISOString(),
            Value: 0
        });
        this.forecastService.addEntity(first);
        this.saveChanges(true);
    };
    /**
     * Copies the values from the selected entity.
     * @param {Entity} entity The selected entity.
     * @returns
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.shrinkageSelected = function (entity) {
        if (!entity) {
            return;
        }
        this.shrinkage['Start'] = entity['Start'];
        this.shrinkage['Finish'] = entity['Finish'];
        this.shrinkage['Value'] = entity['Value'];
    };
    /**
     * Creates a new shrinkage and fits it in the existing data set.
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.add = function (event) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var newStart, newFinish, newValue, smallerTotalOverlaps, largerTotalOverlap, splitShrinkage, startOverlap, finishOverlap;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (event) {
                            event.target.classList.add('sym-working');
                        }
                        newStart = moment.duration(this.shrinkage['Start']);
                        newFinish = moment.duration(this.shrinkage['Finish']);
                        if (newStart.asMilliseconds() >= newFinish.asMilliseconds()) {
                            this.alertsService.createAlert('warning', 'APP.FORECAST.SHRINKAGE_INVALID_TIMESLOT');
                            return [2 /*return*/];
                        }
                        if (!newStart.isValid() || newStart.asDays() >= 1 || !newFinish.isValid() || newFinish.asDays() >= 1) {
                            this.alertsService.createAlert('warning', 'APP.FORECAST.SHRINKAGE_INVALID_TIME');
                            return [2 /*return*/];
                        }
                        newValue = parseInt(this.shrinkage['Value']);
                        if (isNaN(newValue) || newValue < 0 || newValue > 99) {
                            this.alertsService.createAlert('warning', 'APP.FORECAST.SHRINKAGE_INVALID_VALUE');
                            return [2 /*return*/];
                        }
                        smallerTotalOverlaps = this.listComponent.items.filter(function (s) {
                            return newStart.asMinutes() <= moment.duration(s['Start']).asMinutes() &&
                                newFinish.asMinutes() >= moment.duration(s['Finish']).asMinutes();
                        });
                        smallerTotalOverlaps.forEach(function (s) {
                            s.entityAspect.setDeleted();
                        });
                        largerTotalOverlap = this.listComponent.items.find(function (s) {
                            return newStart.asMinutes() > moment.duration(s['Start']).asMinutes() &&
                                newFinish.asMinutes() < moment.duration(s['Finish']).asMinutes();
                        });
                        if (largerTotalOverlap) {
                            splitShrinkage = this.listComponent.entityType.createEntity({
                                DayOfWeek: this.day,
                                FunctionKey: this.functionKey,
                                Start: newFinish.toISOString(),
                                Finish: largerTotalOverlap['Finish'],
                                Value: largerTotalOverlap['Value']
                            });
                            this.forecastService.addEntity(splitShrinkage);
                            largerTotalOverlap['Finish'] = newStart.toISOString();
                        }
                        else {
                            startOverlap = this.listComponent.items.find(function (s) {
                                return !s.entityAspect.entityState.isDeleted() &&
                                    newStart.asMinutes() <= moment.duration(s['Start']).asMinutes() &&
                                    newFinish.asMinutes() > moment.duration(s['Start']).asMinutes();
                            });
                            if (startOverlap) {
                                startOverlap['Start'] = newFinish.toISOString();
                            }
                            finishOverlap = this.listComponent.items.find(function (s) {
                                return !s.entityAspect.entityState.isDeleted() &&
                                    newStart.asMinutes() < moment.duration(s['Finish']).asMinutes() &&
                                    newFinish.asMinutes() >= moment.duration(s['Finish']).asMinutes();
                            });
                            if (finishOverlap) {
                                finishOverlap['Finish'] = newStart.toISOString();
                            }
                        }
                        this.forecastService.addEntity(this.shrinkage);
                        return [4 /*yield*/, this.saveChanges()];
                    case 1:
                        _a.sent();
                        this.resetNewShrinkage();
                        if (event) {
                            event.target.classList.remove('sym-working');
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Sets the shrinkage to be added.
     * @private
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.resetNewShrinkage = function () {
        this.shrinkage = this.shrinkageType.createEntity({
            DayOfWeek: this.day,
            FunctionKey: this.functionKey,
            Start: moment.duration().toISOString(),
            Finish: moment.duration('23:59').toISOString(),
            Value: 0
        });
    };
    /**
     * Saves changes to the forecast service and reloads the data set.
     * @private
     * @param {boolean} first A value indicating whether the first shrinkage is saved.
     * @memberOf ShrinkageComponent
     */
    ShrinkageComponent.prototype.saveChanges = function (first) {
        if (first === void 0) { first = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.forecastService.saveChanges(function (error) {
                            if (error) {
                                _this.alertsService.createAlert('danger', 'APP.FORECAST.SHRINKAGE_NOT_SAVED');
                                console.error(error);
                                // #4061: The changes are not done by the user, so no alert.
                            }
                            else if (!first) {
                                _this.alertsService.createAlert('success', 'APP.FORECAST.SHRINKAGE_SUCCESSFULLY_ADDED');
                            }
                        }).toPromise()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.listComponent.refresh()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    return ShrinkageComponent;
}());
export { ShrinkageComponent };
